import { useEffect } from 'react';

type HeadAttributes = Record<string, string>;

export type HeadLink = HeadAttributes & {
  key: string;
  href: string;
  rel: string;
};

export type HeadMeta = HeadAttributes & {
  key: string;
  content: string;
  name: string;
};

type HeadMetadataProps = {
  links: HeadLink[];
  meta: HeadMeta[];
  title: string;
};

const managedHeadKey = 'studioHeadKey';

const syncAttributes = (element: HTMLElement, attributes: HeadAttributes): void => {
  const managedAttributes = new Set(['data-studio-head-key', ...Object.keys(attributes)]);

  for (const attribute of element.getAttributeNames()) {
    if (!managedAttributes.has(attribute)) {
      element.removeAttribute(attribute);
    }
  }

  for (const [attribute, value] of Object.entries(attributes)) {
    element.setAttribute(attribute, value);
  }
};

const findManagedElement = <ElementType extends HTMLElement>(
  tagName: string,
  key: string
): ElementType | undefined => {
  return Array.from(
    document.head.querySelectorAll<ElementType>(`${tagName}[data-studio-head-key]`)
  ).find((element) => element.dataset[managedHeadKey] === key);
};

const upsertManagedElement = <ElementType extends HTMLElement>(
  tagName: string,
  key: string,
  attributes: HeadAttributes
): ElementType => {
  const element = findManagedElement<ElementType>(tagName, key) ?? document.createElement(tagName);

  if (!element.isConnected) {
    document.head.append(element);
  }

  syncAttributes(element, {
    'data-studio-head-key': key,
    ...attributes
  });

  return element as ElementType;
};

export const applyHeadMetadata = ({ links, meta, title }: HeadMetadataProps): void => {
  document.title = title;

  for (const link of links) {
    const { key, ...attributes } = link;
    upsertManagedElement<HTMLLinkElement>('link', key, attributes);
  }

  for (const item of meta) {
    const { key, ...attributes } = item;
    upsertManagedElement<HTMLMetaElement>('meta', key, attributes);
  }
};

export const HeadMetadata = ({ links, meta, title }: HeadMetadataProps): null => {
  useEffect(() => {
    // Keep head updates effect-based so StrictMode avoids react-helmet's unsafe lifecycle warning.
    applyHeadMetadata({ links, meta, title });
  }, [links, meta, title]);

  return null;
};
